import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { PieChart } from "@mui/x-charts";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

/**
 * CompanyPieChart component for displaying credits breakdown by company using a pie chart
 * with legend only visible on hover
 *
 * @param {Object[]} creditsByCompany - Array of objects containing company and credits data
 * @returns {JSX.Element} - The rendered company pie chart component
 */
export default function CompanyPieChart({ creditsByCompany = [] }) {
  const theme = useTheme();

  // Check if we have data to display
  if (!creditsByCompany || creditsByCompany.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          No company data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Format data for PieChart - ensure all required fields exist and limit to top companies
  const data = creditsByCompany
    .filter(
      (item) =>
        item &&
        typeof item === "object" &&
        "company" in item &&
        "credits" in item &&
        item.credits > 0 // Only show companies with credits > 0
    )
    .slice(0, 8) // Limit to top 8 companies for better readability in pie chart
    .map((item, index) => ({
      id: index,
      value: typeof item.credits === "number" ? item.credits : 0,
      label: item.company || "Unknown Company",
    }));

  // If we have no valid data points after filtering, show the no data message
  if (data.length === 0) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant='body1'
          color='text.secondary'
        >
          No valid company data available for the selected period
        </Typography>
      </Box>
    );
  }

  // Handle chart rendering in a try-catch block to prevent runtime errors
  try {
    return (
      <React.Fragment>
        <div
          style={{
            width: "100%",
            flexGrow: 1,
            overflow: "hidden",
            height: "100%",
          }}
        >
          <PieChart
            series={[
              {
                data: data,
                highlightScope: { faded: "global", highlighted: "item" },
                faded: {
                  innerRadius: 20,
                  additionalRadius: -20,
                  color: "gray",
                },
                valueFormatter: (value) => `${value.value} credits`,
                innerRadius: 20,
                outerRadius: 70,
                paddingAngle: 1,
                cornerRadius: 3,
              },
            ]}
            margin={{
              top: 20,
              right: 20,
              left: 20,
              bottom: 20,
            }}
            slotProps={{
              legend: {
                hidden: true, // Hide legend by default
              },
            }}
            tooltip={{
              trigger: "item",
              formatter: (params) => {
                return `${params.label}: ${params.value} credits (${params.percent}%)`;
              },
            }}
            sx={{
              height: 190,
              "& .MuiChartsTooltip-root": {
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: theme.shape.borderRadius,
                boxShadow: theme.shadows[3],
              },
            }}
          />
        </div>
      </React.Fragment>
    );
  } catch (error) {
    console.error("Error rendering company pie chart:", error);
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <Typography
          variant='body1'
          color='error'
        >
          Error rendering company pie chart
        </Typography>
        <Typography
          variant='body2'
          color='text.secondary'
        >
          Please try adjusting the date range or contact support
        </Typography>
      </Box>
    );
  }
}
